import { useState } from 'react';
import { useLocation } from 'react-router';
import { Meta } from '@/components/common';
import { LoginForm, MfaChallengeForm } from '@/components/features/auth';
import { AuthLayout } from '@/components/layout';
import { CUSTOMER_AUTH_COPY, SUCCESS } from '@/constants';
import type { MfaChallenge } from '@/types';

/**
 * Signing in, both steps - the landing page (/) links here.
 *
 * One route rather than two, because the challenge only exists in memory: it is
 * single-use and short-lived, and a `/login/mfa` URL would be a page that is
 * broken on reload and bookmarkable into a dead end. Holding it in state means a
 * reload correctly starts over.
 */
export function LoginPage() {
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const location = useLocation();

  /*
   * Set by the reset flow on its way here. Read defensively - history state is
   * whatever the browser has, including from an older build of this app.
   */
  const state = location.state as { passwordReset?: unknown; email?: unknown } | null;
  const justReset = state?.passwordReset === true;
  const email = typeof state?.email === 'string' ? state.email : '';

  return (
    <>
      <Meta title="Sign in" description="Sign in to manage your NuDent licences." />

      <AuthLayout {...CUSTOMER_AUTH_COPY}>
        {challenge ? (
          <MfaChallengeForm challenge={challenge} onStartOver={() => setChallenge(null)} />
        ) : (
          <LoginForm
            initialEmail={email}
            {...(justReset ? { notice: SUCCESS.passwordReset } : {})}
            onChallenge={setChallenge}
          />
        )}
      </AuthLayout>
    </>
  );
}
